/**
 * Atomic color tokens for consistent tinted backgrounds, text, and badges.
 *
 * Components compose these atoms instead of duplicating raw Tailwind strings.
 * Only the "soft" recipe (100-shade bg / 500-shade dark bg) is covered here;
 * components with bespoke opacity or shade needs keep their own maps.
 */

export type ColorName =
  | "amber"
  | "blue"
  | "cyan"
  | "emerald"
  | "gray"
  | "green"
  | "indigo"
  | "orange"
  | "purple"
  | "red"
  | "rose"
  | "sky"
  | "slate"
  | "violet"
  | "yellow";

/** Soft tinted background — `bg-{color}-100 dark:bg-{color}-500/20`. */
export const COLOR_BG: Record<ColorName, string> = {
  amber: "bg-amber-100 dark:bg-amber-500/20",
  blue: "bg-slate-100 dark:bg-slate-500/20",
  cyan: "bg-cyan-100 dark:bg-cyan-500/20",
  emerald: "bg-emerald-100 dark:bg-emerald-500/20",
  gray: "bg-gray-100 dark:bg-gray-500/20",
  green: "bg-green-100 dark:bg-green-500/20",
  indigo: "bg-emerald-100 dark:bg-emerald-500/20",
  orange: "bg-orange-100 dark:bg-orange-500/20",
  purple: "bg-slate-100 dark:bg-slate-500/20",
  red: "bg-red-100 dark:bg-red-500/20",
  rose: "bg-rose-100 dark:bg-rose-500/20",
  sky: "bg-slate-100 dark:bg-slate-500/20",
  slate: "bg-slate-100 dark:bg-slate-500/20",
  violet: "bg-violet-100 dark:bg-violet-500/20",
  yellow: "bg-yellow-100 dark:bg-yellow-500/20",
};

/** Standard text — `text-{color}-600 dark:text-{color}-400`. */
export const COLOR_TEXT: Record<ColorName, string> = {
  amber: "text-amber-600 dark:text-amber-400",
  blue: "text-slate-600 dark:text-slate-400",
  cyan: "text-cyan-600 dark:text-cyan-400",
  emerald: "text-emerald-600 dark:text-emerald-400",
  gray: "text-gray-600 dark:text-gray-400",
  green: "text-green-600 dark:text-green-400",
  indigo: "text-emerald-600 dark:text-emerald-400",
  orange: "text-orange-600 dark:text-orange-400",
  purple: "text-slate-600 dark:text-slate-400",
  red: "text-red-600 dark:text-red-400",
  rose: "text-rose-600 dark:text-rose-400",
  sky: "text-slate-600 dark:text-slate-400",
  slate: "text-slate-600 dark:text-slate-400",
  violet: "text-violet-600 dark:text-violet-400",
  yellow: "text-yellow-600 dark:text-yellow-400",
};

/** Emphasis text — `text-{color}-700 dark:text-{color}-300`. */
export const COLOR_TEXT_EMPHASIS: Record<ColorName, string> = {
  amber: "text-amber-700 dark:text-amber-300",
  blue: "text-slate-700 dark:text-slate-300",
  cyan: "text-cyan-700 dark:text-cyan-300",
  emerald: "text-emerald-700 dark:text-emerald-300",
  gray: "text-gray-700 dark:text-gray-300",
  green: "text-green-700 dark:text-green-300",
  indigo: "text-emerald-700 dark:text-emerald-300",
  orange: "text-orange-700 dark:text-orange-300",
  purple: "text-slate-700 dark:text-slate-300",
  red: "text-red-700 dark:text-red-300",
  rose: "text-rose-700 dark:text-rose-300",
  sky: "text-slate-700 dark:text-slate-300",
  slate: "text-slate-700 dark:text-slate-300",
  violet: "text-violet-700 dark:text-violet-300",
  yellow: "text-yellow-700 dark:text-yellow-300",
};

/** Combined soft bg + standard text — convenience for icons, badges, etc. */
export const COLOR_SOFT: Record<ColorName, string> = {
  amber: `${COLOR_TEXT.amber} ${COLOR_BG.amber}`,
  blue: `${COLOR_TEXT.blue} ${COLOR_BG.blue}`,
  cyan: `${COLOR_TEXT.cyan} ${COLOR_BG.cyan}`,
  emerald: `${COLOR_TEXT.emerald} ${COLOR_BG.emerald}`,
  gray: `${COLOR_TEXT.gray} ${COLOR_BG.gray}`,
  green: `${COLOR_TEXT.green} ${COLOR_BG.green}`,
  indigo: `${COLOR_TEXT.indigo} ${COLOR_BG.indigo}`,
  orange: `${COLOR_TEXT.orange} ${COLOR_BG.orange}`,
  purple: `${COLOR_TEXT.purple} ${COLOR_BG.purple}`,
  red: `${COLOR_TEXT.red} ${COLOR_BG.red}`,
  rose: `${COLOR_TEXT.rose} ${COLOR_BG.rose}`,
  sky: `${COLOR_TEXT.sky} ${COLOR_BG.sky}`,
  slate: `${COLOR_TEXT.slate} ${COLOR_BG.slate}`,
  violet: `${COLOR_TEXT.violet} ${COLOR_BG.violet}`,
  yellow: `${COLOR_TEXT.yellow} ${COLOR_BG.yellow}`,
};

// ---------------------------------------------------------------------------
// Signal accent hexes — raw color literals for surfaces that can't take a
// Tailwind class: inline `style={{ backgroundColor }}`, SVG fill/stroke,
// framer-motion `style`, and `color`/`accentColor` props on charts, sparklines,
// and avatars.
//
// Hex twins of the semantic hues at the 500 shade. FIXED across light/dark on
// Purpose — a data-viz dot or sparkline must read the same hue in both themes,
// Unlike `theme-constants.ts` `status.*`, which shifts per mode for page chrome.
// These are NOT for categorical chart palettes (donut slices) or multi-stop
// Gradients — those are bespoke sequences, not flat semantic accents.
// ---------------------------------------------------------------------------

export const SIGNAL_HEX = {
  /** Identity / compact accent — violet-500. */
  accent: "#8b5cf6",
  /** Nominal-growth baseline accent — purple-500. Distinct from `accent`: a
   *  Growth meter treats "normal" as purple, not the healthy-emerald used by
   *  Rate meters. Kept separate so the two intents stay legible. */
  accentGrowth: "#a855f7",
  /** Danger / spike / offline — rose-500. */
  danger: "#f43f5e",
  /** Healthy / good / hot / running — emerald-500. */
  healthy: "#10b981",
  /** Info / age / cold — sky-500. */
  info: "#0ea5e9",
  /** Muted default fill — slate-300, one step lighter than neutral. */
  muted: "#cbd5e1",
  /** Neutral / idle / no-data — slate-500. */
  neutral: "#64748b",
  /** Warning / elevated / warm / parked — amber-500. */
  warning: "#f59e0b",
} as const;
